import React, { useState, useEffect } from "react";
import { useAuth } from "../../../lib/AuthContext";
import XanoAPI from "../../../lib/XanoAPI";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Download } from "lucide-react";
import { useToast } from "../../ui/use-toast";
import { formatCurrency } from "../../../lib/utils";

interface AgentPayout {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  processed_at: string | null;
  payment_method: string;
  reference: string;
}

// Mock data for testing the UI
const generateMockPayouts = (): AgentPayout[] => {
  const statuses = ["completed", "pending", "failed"];
  // Fixed payment method to Stripe
  const paymentMethod = "Stripe";

  // Generate between 1-4 entries
  const numEntries = Math.floor(Math.random() * 4) + 1;

  return Array.from({ length: numEntries }, (_, i) => {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const amount = Math.floor(Math.random() * 10000) + 500;
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 90)); // Random date within last 90 days

    return {
      id: `payout-${i + 1}`,
      amount,
      status,
      created_at: date.toISOString(),
      processed_at:
        status === "completed"
          ? new Date(date.getTime() + 86400000).toISOString()
          : null,
      payment_method: paymentMethod,
      reference: `PAY-${Math.floor(Math.random() * 10000)}`,
    };
  });
};

export default function AgentPayoutHistory() {
  const { currentUser } = useAuth();
  const api = XanoAPI.getInstance();
  const { toast } = useToast();
  const [payouts, setPayouts] = useState<AgentPayout[]>([]);
  const [loading, setLoading] = useState(true);
  // Set to true to use mock data instead of API calls
  const useMockData = true;

  useEffect(() => {
    if (currentUser?.id) {
      loadPayouts();
    }
  }, [currentUser]);

  const loadPayouts = async () => {
    try {
      if (useMockData) {
        // Use mock data for testing
        setTimeout(() => {
          setPayouts(generateMockPayouts());
          setLoading(false);
        }, 1000); // Simulate network delay
        return;
      }

      // First try the agent-specific endpoint
      const response = await api.getAgentPayouts(currentUser?.id);

      if (response.success && response.data) {
        setPayouts(response.data);
      } else {
        // Fallback to the general payouts endpoint
        const fallbackResponse = await api.getPayouts(currentUser?.id);
        // Transform the fallback response to match AgentPayout interface
        const transformedPayouts: AgentPayout[] = fallbackResponse.map(
          (payout) => ({
            id: payout.id.toString(),
            amount: payout.amount,
            status: payout.status,
            created_at: payout.created_at,
            processed_at: payout.completed_at || null,
            payment_method: "Direct Deposit", // Default value
            reference: `PAY-${payout.id}`,
          })
        );
        setPayouts(transformedPayouts);
      }
    } catch (error) {
      console.error("Error loading payouts:", error);
      // Fallback to mock data if API call fails
      setPayouts(generateMockPayouts());
      toast({
        title: "Using Mock Data",
        description:
          "Failed to load payout history. Showing mock data instead.",
        variant: "default",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["Date", "Amount", "Status", "Payment Method", "Reference"];
    const rows = payouts.map((payout) => [
      new Date(payout.created_at).toLocaleDateString(),
      formatCurrency(payout.amount),
      payout.status,
      payout.payment_method,
      payout.reference,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `payout-history-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <Badge variant="default">Completed</Badge>;
      case "pending":
        return <Badge variant="secondary">Pending</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!currentUser) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        Please sign in to view your payout history.
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-accent">Payout History</h2>
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Recent Payouts</h3>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-6">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : payouts.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No payout history available
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>
                      {new Date(payout.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{formatCurrency(payout.amount)}</TableCell>
                    <TableCell>{getStatusBadge(payout.status)}</TableCell>
                    <TableCell>{payout.payment_method}</TableCell>
                    <TableCell>{payout.reference}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
